import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { authApi } from "@features/auth/api/auth-api";
import { authStorage } from "@features/auth/lib/auth-storage";
import { normalizeUser } from "@features/auth/lib/auth-utils";
import type { AuthUser, LoginInput } from "@features/auth/types/types";

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  login(input: LoginInput): Promise<void>;
  refreshUser(cacheBustProfileImage?: boolean): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isInitializing, setInitializing] = useState(true);

  useEffect(() => {
    const session = authStorage.get();
    if (!session) {
      setInitializing(false);
      return;
    }
    setUser(session.user);
    setAccessToken(session.accessToken);
    authApi
      .me()
      .then(({ data }) => {
        const currentUser = normalizeUser(data.data);
        setUser(currentUser);
        authStorage.set({ ...session, user: currentUser });
      })
      .catch(() => {
        authStorage.clear();
        setUser(null);
        setAccessToken(null);
      })
      .finally(() => setInitializing(false));
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const { data } = await authApi.login(input);
    const user = normalizeUser(data.data.user);
    const session = {
      accessToken: data.data.accessToken,
      refreshToken: data.data.refreshToken,
      user,
      rememberMe: input.rememberMe,
    };
    authStorage.set(session);
    setUser(user);
    setAccessToken(session.accessToken);
    window.location.replace("/dashboard");
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      authStorage.clear();
      setUser(null);
      setAccessToken(null);
      window.location.replace("/login");
    }
  }, []);

  const refreshUser = useCallback(async (cacheBustProfileImage = false) => {
    const session = authStorage.get();
    if (!session) return;
    const { data } = await authApi.me();
    const currentUser = normalizeUser(data.data);
    if (cacheBustProfileImage && currentUser.profileImageUrl) {
      const separator = currentUser.profileImageUrl.includes("?") ? "&" : "?";
      currentUser.profileImageUrl = `${currentUser.profileImageUrl}${separator}v=${Date.now()}`;
    }
    setUser(currentUser);
    authStorage.set({ ...session, user: currentUser });
  }, []);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      isAuthenticated: Boolean(user && accessToken),
      isInitializing,
      login,
      refreshUser,
      logout,
    }),
    [user, accessToken, isInitializing, login, refreshUser, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
